<template>
    <section class="password-token-page" :style="themePageStyle">
        <div class="password-token-shell">
            <main class="password-token-card">
                <form class="password-token-form" @submit="post">
                    <div class="password-token-header">
                        <img src="/images/logo-lead.png" :alt="themeBrandName" />
                        <span class="password-token-eyebrow"><i class="fas fa-shield-alt" aria-hidden="true"></i> Sécurité</span>
                        <h1>{{ welcome ? "Créer mon mot de passe" : "Mot de passe perdu" }}</h1>
                        <p>
                            {{
                                welcome
                                    ? "Choisissez un mot de passe sécurisé pour finaliser votre accès."
                                    : "Définissez un nouveau mot de passe pour retrouver l'accès à votre compte."
                            }}
                        </p>
                    </div>

                    <LoaderComponent v-if="loading" />
                    <template v-else>
                        <div class="mb-3 password-token-field">
                            <label class="form-label" for="email">Votre adresse e-mail</label>
                            <input
                                id="email"
                                type="email"
                                class="form-control"
                                :class="{ 'is-invalid': hasErrors('email') }"
                                name="email"
                                :value="email_"
                                required
                                disabled />
                            <ShowErrors :errors="errors" errorKey="email" />
                        </div>

                        <div class="mb-3 password-token-field">
                            <label class="form-label" for="password">{{ welcome ? "Mot de passe" : "Nouveau mot de passe" }}</label>
                            <PasswordInputComponent id="password" v-model="user.password" @input="clearPasswordErrors" />
                            <ShowErrors :errors="errors" errorKey="password" />
                        </div>

                        <div class="mb-3 password-token-field">
                            <label class="form-label" for="password-confirm">Confirmez le mot de passe</label>
                            <PasswordConfirmationInputComponent
                                id="password-confirm"
                                :mainPassword="user.password"
                                v-model="user.password_confirmation"
                                @input="clearErrors('password_confirmation')" />
                            <ShowErrors :errors="errors" errorKey="password_confirmation" />
                        </div>

                        <button type="submit" class="btn btn-success btn-circle w-100 password-token-submit">
                            {{ welcome ? "Créer mon mot de passe" : "Réinitialiser mon mot de passe" }}
                        </button>
                    </template>
                </form>
            </main>

            <aside class="password-token-visual" aria-hidden="true">
                <img class="password-token-visual__image" src="/images/illu-login.jpg" alt="" />
                <div class="password-token-visual__content">
                    <div class="password-token-visual__badge">Compte sécurisé</div>
                    <p>Vos accès restent protégés pendant la création du nouveau mot de passe.</p>
                </div>
            </aside>
        </div>
    </section>
</template>

<script>
import LoaderComponent from "../../components/LoaderComponent.vue";
import ShowErrors from "../../components/errors/ShowErrors.vue";
import PasswordInputComponent from "../../components/forms/PasswordInputComponent.vue";
import PasswordConfirmationInputComponent from "../../components/forms/PasswordConfirmationInputComponent.vue";
export default {
    data() {
        return {
            loading: false,
            errors: null,
            user: {
                password: null,
                password_confirmation: null,
            },
        };
    },
    computed: {
        welcome() {
            return this.$route.query.welcome == 1;
        },
        token() {
            return this.$route.params.token;
        },
        email_() {
            return this.$route.query.email;
        },
        themePageStyle() {
            return {
                "--theme-main-color": this.$style["main-color"] || "#083e60",
                "--theme-main-color-rgb": this.$style["main-color-rgb"] || "8, 62, 96",
                "--theme-main-color-light": this.$style["main-color-light"] || "#30a4b7",
                "--theme-main-color-light-rgb": this.$style["main-color-light-rgb"] || "48, 164, 183",
                "--theme-auth-page-background":
                    this.$style["auth-page-background"] || "linear-gradient(135deg, #ffffff 0%, #f6fbfd 48%, #eef8fb 100%)",
                "--theme-auth-text-color": this.$style["auth-text-color"] || "#16323d",
                "--theme-auth-muted-color": this.$style["auth-muted-color"] || "#647984",
                "--theme-auth-label-color": this.$style["auth-label-color"] || "#263f4a",
                "--theme-auth-field-border-rgb": this.$style["auth-field-border-rgb"] || "38, 63, 74",
                "--theme-auth-success-shadow-rgb": this.$style["auth-success-shadow-rgb"] || "48, 164, 183",
                "--theme-auth-visual-shadow-rgb": this.$style["auth-visual-shadow-rgb"] || "8, 62, 96",
                "--theme-auth-visual-overlay-rgb": this.$style["auth-visual-overlay-rgb"] || "10, 50, 67",
                "--theme-auth-visual-panel-rgb": this.$style["auth-visual-panel-rgb"] || "9, 43, 58",
            };
        },
    },
    methods: {
        post(e) {
            if (e) e.preventDefault();
            this.loading = true;
            this.errors = null;
            this.$store
                .dispatch("auth/resetPasswordWithToken", {
                    ...this.user,
                    email: this.email_,
                    token: this.token,
                })
                .then(() => {
                    window.location.href = "/";
                })
                .catch((error) => {
                    this.loading = false;
                    this.user.password = "";
                    this.user.password_confirmation = "";
                    if (error.response && error.response.data.errors) {
                        this.errors = error.response.data.errors;
                    } else {
                        this.$notify({
                            group: "notification",
                            type: "error",
                            title: this.getErrorMsgFromErrorResponse(error),
                        });
                    }
                });
        },
        clearPasswordErrors() {
            this.clearErrors("password");
            this.clearErrors("password_confirmation");
        },
        clearErrors(key) {
            if (!this.errors || !this.errors[key]) return;

            this.$delete(this.errors, key);

            if (Object.keys(this.errors).length === 0) {
                this.errors = null;
            }
        },
        hasErrors(key) {
            return this.errors !== null && this.errors[key] && this.errors[key].length > 0;
        },
    },
    components: {
        LoaderComponent,
        ShowErrors,
        PasswordInputComponent,
        PasswordConfirmationInputComponent,
    },
};
</script>

<style lang="scss" scoped>
.password-token-page {
    width: 100%;
    min-height: 100vh;
    --theme-main-color: #083e60;
    --theme-main-color-rgb: 8, 62, 96;
    --theme-main-color-light: #30a4b7;
    --theme-main-color-light-rgb: 48, 164, 183;
    --theme-auth-page-background: linear-gradient(135deg, #ffffff 0%, #f6fbfd 48%, #eef8fb 100%);
    --theme-auth-text-color: #16323d;
    --theme-auth-muted-color: #647984;
    --theme-auth-label-color: #263f4a;
    --theme-auth-field-border-rgb: 38, 63, 74;
    --theme-auth-success-shadow-rgb: 48, 164, 183;
    --theme-auth-visual-shadow-rgb: 8, 62, 96;
    --theme-auth-visual-overlay-rgb: 10, 50, 67;
    --theme-auth-visual-panel-rgb: 9, 43, 58;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 18px;
    background:
        radial-gradient(circle at top left, rgba(var(--theme-main-color-light-rgb), 0.16), transparent 32%), var(--theme-auth-page-background);
}

.password-token-shell {
    width: 100%;
    max-width: 1120px;
    display: grid;
    grid-template-columns: minmax(340px, 480px) minmax(280px, 1fr);
    gap: 28px;
    align-items: center;
}

.password-token-card {
    width: 100%;
    border: 1px solid rgba(var(--theme-main-color-rgb), 0.12);
    border-radius: 22px;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 22px 60px rgba(var(--theme-main-color-rgb), 0.12);
}

.password-token-form {
    padding: 38px;
}

.password-token-header {
    margin-bottom: 28px;
}

.password-token-header img {
    width: 178px;
    margin-bottom: 28px;
}

.password-token-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 26px;
    margin-bottom: 14px;
    padding: 4px 10px;
    border: 1px solid rgba(var(--theme-main-color-light-rgb), 0.22);
    border-radius: 999px;
    background: rgba(var(--theme-main-color-light-rgb), 0.1);
    color: var(--theme-main-color-light);
    font-size: 12px;
    font-weight: 600;
}

.password-token-header h1 {
    margin: 0 0 12px;
    color: var(--theme-auth-text-color);
    font-size: 28px;
    font-weight: 700;
    line-height: 1.2;
}

.password-token-header p {
    margin: 0;
    color: var(--theme-auth-muted-color);
    font-size: 15px;
    line-height: 1.6;
}

.password-token-field {
    margin-bottom: 20px;
}

.password-token-field label {
    margin-bottom: 9px;
    color: var(--theme-auth-label-color);
    font-size: 14px;
    font-weight: 600;
}

.password-token-field .form-control {
    min-height: 48px;
    border-color: rgba(var(--theme-auth-field-border-rgb), 0.14);
    border-radius: 14px;
    color: var(--theme-auth-text-color);
    font-size: 15px;
    box-shadow: none;
}

.password-token-field .form-control:focus {
    border-color: rgba(var(--theme-main-color-light-rgb), 0.72);
    box-shadow: 0 0 0 0.2rem rgba(var(--theme-main-color-light-rgb), 0.12);
}

.password-token-submit {
    min-height: 48px;
    margin-top: 4px;
    font-weight: 700;
    box-shadow: 0 12px 28px rgba(var(--theme-auth-success-shadow-rgb), 0.22);
}

.password-token-visual {
    position: relative;
    min-height: 560px;
    overflow: hidden;
    border: 10px solid #ffffff;
    border-radius: 30px;
    background: #ffffff;
    box-shadow: 0 24px 70px rgba(var(--theme-auth-visual-shadow-rgb), 0.14);
}

.password-token-visual__image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.password-token-visual::before {
    content: "";
    position: absolute;
    z-index: 1;
    inset: 0;
    background:
        radial-gradient(circle at 24% 18%, rgba(255, 255, 255, 0.18), transparent 28%),
        linear-gradient(135deg, rgba(var(--theme-main-color-rgb), 0.18), rgba(var(--theme-main-color-light-rgb), 0.08)),
        linear-gradient(180deg, rgba(var(--theme-auth-visual-overlay-rgb), 0), rgba(var(--theme-auth-visual-overlay-rgb), 0.3));
}

.password-token-visual__content {
    position: absolute;
    z-index: 2;
    left: 30px;
    right: 30px;
    bottom: 30px;
    padding: 22px;
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 20px;
    background: rgba(var(--theme-auth-visual-panel-rgb), 0.28);
    color: #ffffff;
    backdrop-filter: blur(12px);
}

.password-token-visual__badge {
    display: inline-flex;
    align-items: center;
    min-height: 28px;
    margin-bottom: 12px;
    padding: 5px 11px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.16);
    color: rgba(255, 255, 255, 0.92);
    font-size: 12px;
    font-weight: 700;
}

.password-token-visual__content p {
    max-width: 360px;
    margin: 0;
    color: rgba(255, 255, 255, 0.88);
    font-size: 17px;
    font-weight: 600;
    line-height: 1.55;
}

@media (max-width: 767.98px) {
    .password-token-shell {
        max-width: 480px;
        display: block;
    }

    .password-token-visual {
        display: none;
    }
}

@media (max-width: 575.98px) {
    .password-token-page {
        align-items: flex-start;
        padding: 28px 14px;
    }

    .password-token-card {
        border-radius: 18px;
    }

    .password-token-form {
        padding: 28px 22px;
    }

    .password-token-header img {
        width: 150px;
        margin-bottom: 24px;
    }
}
</style>
